import React, { useState, useEffect } from 'react';
import { Table, Space, Select, Row, Col, Modal, DatePicker, Input, Button, Tabs, Form } from 'antd';
import { useAuth } from '../contexts/AuthContext';
import { getLoansByFilters, getConsultants, updateInvoiceData } from '../services/loanService';
import { Link } from 'react-router-dom';
import locale from 'antd/es/date-picker/locale/de_DE';
import { useTranslation } from 'react-i18next';
import { FileTextOutlined, BankOutlined, EuroOutlined } from '@ant-design/icons';

import moment from 'moment';
import Page from '../components/layout/Page';
import MetricCard from '../components/MetricCard';

const { Option } = Select;
const { TabPane } = Tabs;

const Invoices = () => {
    const { user } = useAuth();
    const { t } = useTranslation();

    const [selectedConsultant, setSelectedConsultant] = useState('');
    const [consultants, setConsultants] = useState([]);
    const [loans, setLoans] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');

    const [showInvoiceNumberModal, setShowInvoiceNumberModal] = useState(false);
    const [showStatementModal, setShowStatementModal] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [selectedLoan, setSelectedLoan] = useState(null);
    const [invoiceNumberData, setInvoiceNumberData] = useState({
        invoice_date: '',
        invoice_number: ''
    });
    const [statementData, setStatementData] = useState({
        statement_received_date: '',
        statement_received_number: ''
    });
    const [paymentData, setPaymentData] = useState({
        payment_date: ''
    });

    const fetchData = async () => {
        setLoading(true);
        try {
            if (user?.groups?.includes('Consultant-Admin')) {
                const consultantsData = await getConsultants();
                setConsultants(consultantsData);
            }

            const filters = {
                consultant: selectedConsultant || undefined,
                status: selectedStatus || undefined,
                needs_invoice: true  // Nur Kredite, die eine Rechnung benötigen
            };

            const loansData = await getLoansByFilters(filters);
            console.log('Geladene Kredite:', loansData);  // Debug-Log
            setLoans(loansData);

        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [selectedConsultant, selectedStatus]);

    const handleInvoiceNumberSubmit = async () => {
        try {
            await updateInvoiceData(selectedLoan.id, {
                ...invoiceNumberData,
                type: 'invoice_number'
            });
            setShowInvoiceNumberModal(false);
            fetchData();
        } catch (error) {
            console.error('Error updating invoice number:', error);
        }
    };

    const handleStatementSubmit = async () => {
        try {
            await updateInvoiceData(selectedLoan.id, {
                ...statementData,
                type: 'statement'
            });
            setShowStatementModal(false);
            fetchData();
        } catch (error) {
            console.error('Error updating statement:', error);
        }
    };

    const handlePaymentSubmit = async () => {
        try {
            await updateInvoiceData(selectedLoan.id, {
                ...paymentData,
                type: 'payment'
            });
            setShowPaymentModal(false);
            fetchData();
        } catch (error) {
            console.error('Error updating payment:', error);
        }
    };

    const renderActions = (record) => {
        switch (record.status) {
            case 'WITHDRAWAL_PASSED':
                return (
                    <Space>
                        <Button 
                            onClick={() => {
                                setSelectedLoan(record);
                                setShowInvoiceNumberModal(true);
                            }}
                            type="default"
                            style={{ 
                                backgroundColor: '#f0f0f0',
                                color: '#000000',
                                border: '1px solid #d9d9d9'
                            }}
                            icon={<FileTextOutlined />}
                        >
                            Rechnungsnummer
                        </Button>
                        <Button
                            onClick={() => {
                                setSelectedLoan(record);
                                setShowStatementModal(true);
                            }}
                            type="default"
                            style={{ 
                                backgroundColor: '#f0f0f0',
                                color: '#000000',
                                border: '1px solid #d9d9d9'
                            }}
                            icon={<BankOutlined />}
                        >
                            Kontoauszug
                        </Button>
                    </Space>
                );
            
            case 'PAYMENT_CONFIRMED':
                return (
                    <Space>
                        <Button
                            onClick={() => {
                                setSelectedLoan(record);
                                setShowPaymentModal(true);
                            }}
                            type="default"
                            style={{ 
                                backgroundColor: '#f0f0f0',
                                color: '#000000',
                                border: '1px solid #d9d9d9'
                            }}
                            icon={<EuroOutlined />}
                        >
                            Zahlung
                        </Button>
                    </Space>
                );
            
            case 'PAID':
                return (
                    <Space>
                        <Button
                            onClick={() => {
                                window.location.href = '/consultant-billing';
                            }}
                            type="default"
                            style={{ 
                                backgroundColor: '#f0f0f0',
                                color: '#000000',
                                border: '1px solid #d9d9d9'
                            }}
                            icon={<FileTextOutlined />}
                        >
                            Zur Beraterabrechnung
                        </Button>
                    </Space>
                );
            
            default:
                return null;
        }
    };

    const columns = [
        {
            title: 'Vertragsnummer',
            dataIndex: 'process_number',
            key: 'process_number',
        },
        {
            title: 'Auftragsdatum',
            dataIndex: 'reported_at',
            key: 'reported_at',
            render: (date) => date ? moment(date).format('DD.MM.YYYY') : '-'
        },
        {
            title: 'Unterschriftsdatum',
            dataIndex: 'signature_date',
            key: 'signature_date',
            render: (date) => date ? moment(date).format('DD.MM.YYYY') : '-'
        },
        {
            title: 'Kunde',
            key: 'customer',
            render: (_, record) => `${record.customer?.name || ''} ${record.customer?.first_name || ''}`
        },
        {
            title: 'Kredit ID',
            dataIndex: 'id',
            key: 'id',
            render: (id) => <Link to={`/loans/${id}`}>{id}</Link>
        },
        {
            title: t('status_label'),
            dataIndex: 'status',
            key: 'status',
            render: (status) => t(`loanStatus.default.${status}`)
        },
        {
            title: 'Berater',
            dataIndex: ['consultant', 'name'],
            key: 'consultant'
        },
        {
            title: 'Volumen',
            dataIndex: 'volume',
            key: 'volume',
            render: (volume) => `${Number(volume).toLocaleString('de-DE')} €`
        },
        {
            title: 'Provision',
            dataIndex: 'provision',
            key: 'provision',
            render: (provision) => `${Number(provision).toLocaleString('de-DE')} €`,
            align: 'right'

        },
        {
            title: 'Abrechnungssumme Bank',
            dataIndex: 'incentive_amount',
            key: 'incentive_amount',
            render: (amount) => amount ? `${Number(amount).toFixed(2).toLocaleString('de-DE')} €`.replace(/\B(?=(\d{3})+(?!\d))/g, '.').replace(/\./g, ' ') : '-',
            align: 'right'
        },
        {
            title: 'Aktionen',
            key: 'actions',
            render: (_, record) => renderActions(record)
        }
    ];

    const filterLoansByStatus = (status) => {
        return loans.filter(loan => loan.status === status);
    };

    const renderStatusTable = (status, title) => {
        const filteredLoans = filterLoansByStatus(status);
        const statusMetrics = {
            totalVolume: filteredLoans.reduce((sum, loan) => sum + (parseFloat(loan.volume) || 0), 0),
            totalProvision: filteredLoans.reduce((sum, loan) => sum + (parseFloat(loan.provision) || 0), 0),
            totalIncentive: filteredLoans.reduce((sum, loan) => sum + (parseFloat(loan.incentive_amount) || 0), 0)
        };

        return (
            <div style={{ marginBottom: '2rem' }}>
                <h2>{title}</h2>
                <Row gutter={16} style={{ marginBottom: '1rem' }}>
                    <Col span={8}>
                        <MetricCard
                            title="Gesamtvolumen"
                            value={`${Number(statusMetrics.totalVolume).toLocaleString('de-DE')} €`}
                        />
                    </Col>
                    <Col span={8}>
                        <MetricCard
                            title="Gesamtprovision"
                            value={`${Number(statusMetrics.totalProvision).toLocaleString('de-DE')} €`}
                        />
                    </Col>
                    <Col span={8}>
                        <MetricCard
                            title="Gesamt Abrechnungssumme Bank"
                            value={`${Number(statusMetrics.totalIncentive).toLocaleString('de-DE')} €`}
                        />
                    </Col>
                </Row>
                <Table
                    dataSource={filteredLoans}
                    columns={columns}
                    loading={loading}
                    rowKey="id"
                    pagination={{ pageSize: 10 }}
                    size="middle"
                />
            </div>
        );
    };

    return (
        <Page>
            <div style={{ marginBottom: '2rem' }}>
                {user?.groups?.includes('Consultant-Admin') && (
                    <Select
                        style={{ width: 200, marginBottom: '1rem' }}
                        placeholder="Berater auswählen"
                        onChange={setSelectedConsultant}
                        allowClear
                    >
                        {consultants.map(consultant => (
                            <Option key={consultant.id} value={consultant.id}>{consultant.name}</Option>
                        ))}
                    </Select>
                )}
                <Select
                    style={{ width: 200, marginBottom: '1rem' }}
                    placeholder="Status auswählen"
                    onChange={setSelectedStatus}
                    allowClear
                >
                    <Option value="WITHDRAWAL_PASSED">Widerrufsverzicht bestätigt</Option>
                    <Option value="PAYMENT_CONFIRMED">Rechnung/Abrechnung</Option>
                    <Option value="PAID">Bezahlt</Option>
                </Select>
            </div>

            {renderStatusTable('WITHDRAWAL_PASSED', 'Kredite mit bestätigtem Widerrufsverzicht')}
            {renderStatusTable('PAYMENT_CONFIRMED', 'Kredite mit Rechnung/Abrechnung')}
            {renderStatusTable('PAID', 'Bezahlte Kredite')}

            <Modal
                title="Rechnungsnummer eingeben"
                visible={showInvoiceNumberModal}
                onOk={handleInvoiceNumberSubmit}
                onCancel={() => setShowInvoiceNumberModal(false)}
            >
                <Form layout="vertical">
                    <Form.Item label="Rechnungsdatum">
                        <DatePicker
                            locale={locale}
                            value={invoiceNumberData.invoice_date ? moment(invoiceNumberData.invoice_date) : null}
                            onChange={(date) => setInvoiceNumberData({
                                ...invoiceNumberData,
                                invoice_date: date ? date.format('YYYY-MM-DD') : ''
                            })}
                            style={{ width: '100%' }}
                        />
                    </Form.Item>
                    <Form.Item label="Rechnungsnummer">
                        <Input
                            value={invoiceNumberData.invoice_number}
                            onChange={(e) => setInvoiceNumberData({
                                ...invoiceNumberData,
                                invoice_number: e.target.value
                            })}
                        />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="Kontoauszug erfassen"
                open={showStatementModal}
                onOk={handleStatementSubmit}
                onCancel={() => setShowStatementModal(false)}
            >
                <Form layout="vertical">
                    <Form.Item label="Eingangsdatum">
                        <DatePicker
                            locale={locale}
                            value={statementData.statement_received_date ? moment(statementData.statement_received_date) : null}
                            onChange={(date) => setStatementData({
                                ...statementData,
                                statement_received_date: date ? date.format('YYYY-MM-DD') : ''
                            })}
                            style={{ width: '100%' }}
                        />
                    </Form.Item>
                    <Form.Item label="Kontoauszugsnummer">
                        <Input
                            value={statementData.statement_received_number}
                            onChange={(e) => setStatementData({
                                ...statementData,
                                statement_received_number: e.target.value
                            })}
                        />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="Zahlung erfassen"
                open={showPaymentModal}
                onOk={handlePaymentSubmit}
                onCancel={() => setShowPaymentModal(false)}
            >
                <Form layout="vertical">
                    <Form.Item label="Zahlungsdatum">
                        <DatePicker
                            locale={locale}
                            value={paymentData.payment_date ? moment(paymentData.payment_date) : null}
                            onChange={(date) => setPaymentData({
                                ...paymentData,
                                payment_date: date ? date.format('YYYY-MM-DD') : ''
                            })}
                            style={{ width: '100%' }}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </Page>
    );
};

export default Invoices;